/* ==========================================================================
   REFINEDSUPPS - motion.js
   AW Media & Marketing

   Runs inside a Shopify theme unchanged. No dependencies.
   Everything degrades to a fully readable static page if this file
   never loads - nothing here is responsible for making content visible
   in the first place.

   WHY THIS IS STRUCTURED AS init(root) RATHER THAN A FLAT SCRIPT
   Reveal-on-scroll elements start at opacity 0 and only become visible when
   this file adds .in to them. Everything used to be bound once, at page load,
   against a snapshot of the DOM. The theme editor re-renders a section every
   time a setting changes, and that new markup had never been observed, so it
   stayed permanently invisible. Editing any animated section made it vanish.

   So: the scoped work lives in init(root) and is re-run whenever Shopify
   re-renders a section. And in the editor nothing waits for a scroll at all,
   because the editor scrolls the preview itself and an element can easily
   never trigger an observer.
   ========================================================================== */
(function () {
  'use strict';

  var $ = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Read on every call, not captured once. Shopify can set this after our
  // script has run, and a stale false would leave edited sections invisible.
  function designMode() { return !!(window.Shopify && window.Shopify.designMode); }

  /* ------------------------------------------------------------------
     SCOPED: re-runs for any section the theme editor re-renders
     ------------------------------------------------------------------ */
  function init(root) {
    root = root || document;

    /* --- hero load sequence --- */
    var hero = root.matches && root.matches('.hero') ? root : $('.hero', root);
    if (hero && !hero.classList.contains('ready')) {
      if (designMode() || reduced) {
        hero.classList.add('ready');
      } else {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { hero.classList.add('ready'); });
        });
      }
    }

    /* --- reveal: rails reveal whole, never per card --- */
    var targets = $$('.rv, .rv-stagger, .sweep', root).filter(function (t) {
      return !t.classList.contains('in');
    });
    if (designMode() || reduced || !('IntersectionObserver' in window)) {
      // no waiting in the editor: the preview scrolls itself and an element
      // that never intersects would stay invisible for good
      targets.forEach(function (t) { t.classList.add('in'); });
    } else {
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.04 });
      targets.forEach(function (t) { io.observe(t); });
    }

    /* --- count up --- */
    var counts = $$('.count', root).filter(function (c) { return !c.dataset.counted; });
    counts.forEach(function (c) { c.dataset.counted = '1'; });
    if (counts.length) {
      if (designMode() || reduced || !('IntersectionObserver' in window)) {
        counts.forEach(function (c) { c.textContent = parseFloat(c.getAttribute('data-count')) || 0; });
      } else {
        var cio = new IntersectionObserver(function (es) {
          es.forEach(function (e) {
            if (e.isIntersecting) { countUp(e.target); cio.unobserve(e.target); }
          });
        }, { threshold: 0.5 });
        counts.forEach(function (c) { cio.observe(c); });
      }
    }

    /* --- pinned delivery story --- */
    var steps = $$('[data-pin-step]', root);
    var pinImgs = $$('[data-pin-img]', root);
    if (steps.length && pinImgs.length && 'IntersectionObserver' in window && !designMode()) {
      var pio = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          if (!e.isIntersecting) return;
          var k = parseInt(e.target.getAttribute('data-pin-step'), 10);
          steps.forEach(function (s) { s.classList.toggle('on', s === e.target); });
          pinImgs.forEach(function (im) {
            im.classList.toggle('on', parseInt(im.getAttribute('data-pin-img'), 10) === k);
          });
        });
      }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
      steps.forEach(function (s) { pio.observe(s); });
    }

    /* --- parallax --- */
    var pars = $$('.par', root);
    if (pars.length && !reduced && !designMode()) {
      pars.forEach(function (el) { parallaxTargets.push(el); });
      drift();
    }
  }

  function countUp(el) {
    var end = parseFloat(el.getAttribute('data-count')) || 0;
    if (reduced) { el.textContent = end; return; }
    var dur = 1100, t0 = null;
    function step(t) {
      if (!t0) t0 = t;
      var p = Math.min(1, (t - t0) / dur);
      var e = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);   // easeOutExpo
      el.textContent = Math.round(end * e);
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* ------------------------------------------------------------------
     GLOBAL: bound once, survives section re-renders on its own
     ------------------------------------------------------------------ */

  /* --- header on scroll --- */
  var lastStuck = false;
  function onScrollHdr() {
    var hdr = $('.hdr');
    if (!hdr) return;
    var stuck = window.scrollY > 40;
    if (stuck !== lastStuck) { hdr.classList.toggle('stuck', stuck); lastStuck = stuck; }
  }
  window.addEventListener('scroll', onScrollHdr, { passive: true });

  /* --- parallax.
     Drift is measured from each element's OWN rect and clamped. Keying it to
     window.scrollY compounds down a long page and eventually throws the art
     clean off its grid. --- */
  var parallaxTargets = [];
  var ticking = false;
  function drift() {
    var vh = window.innerHeight;
    parallaxTargets = parallaxTargets.filter(function (el) { return el.isConnected; });
    parallaxTargets.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) return;
      var amt = parseFloat(el.getAttribute('data-par')) || 8;
      var prog = ((r.top + r.height / 2) - vh / 2) / (vh / 2);
      prog = Math.max(-1, Math.min(1, prog));
      el.style.transform = 'translate3d(0,' + (prog * amt).toFixed(2) + 'px,0) scale(1.08)';
    });
    ticking = false;
  }
  window.addEventListener('scroll', function () {
    if (!ticking) { ticking = true; requestAnimationFrame(drift); }
  }, { passive: true });
  window.addEventListener('resize', drift, { passive: true });

  /* --- cart count bump.
     Delegated, so it keeps working after the drawer is re-rendered. --- */
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-add-btn]')) return;
    setTimeout(function () {
      $$('[data-cart-count]').forEach(function (c) {
        c.classList.remove('bump');
        void c.offsetWidth;      // restart the animation
        c.classList.add('bump');
      });
    }, 30);
  });

  /* ------------------------------------------------------------------
     BOOT + THEME EDITOR
     ------------------------------------------------------------------ */
  // In the editor, mark the document so CSS can disable the reveal outright.
  // Belt and braces: with the animation off, no combination of re-render,
  // event timing or observer behaviour can leave a section invisible. This is
  // the guarantee, the JavaScript below is only the nicety.
  // THE GUARANTEE.
  // The stylesheet only hides reveal elements when <html> carries .motion-on,
  // and only this line adds it. So content is visible unless this script is
  // definitely running and has taken responsibility for revealing it:
  //   - script fails to load or throws  -> nothing hidden
  //   - theme editor                    -> nothing hidden
  //   - reduced motion                  -> nothing hidden
  // The header of this file always claimed nothing here is responsible for
  // making content visible. Until now that was not true, and an edited section
  // came out as an empty black box.
  function flagDesignMode() {
    if (designMode()) document.documentElement.classList.add('shopify-design-mode');
  }
  flagDesignMode();
  setTimeout(flagDesignMode, 500);   // Shopify can set the flag after we run

  if (!designMode() && !reduced) {
    document.documentElement.classList.add('motion-on');
  }

  init(document);
  onScrollHdr();

  // Shopify's editor events, when they fire.
  ['shopify:section:load', 'shopify:section:reorder', 'shopify:section:select',
   'shopify:block:select', 'shopify:section:unload'].forEach(function (evt) {
    document.addEventListener(evt, function (e) { init(e.target || document); });
  });

  // ...and a MutationObserver, because relying on those events firing exactly
  // as expected is too fragile for something that decides whether content is
  // visible at all. This catches markup arriving from ANY source: the editor,
  // the Section Rendering API, an app injecting into the page.
  if ('MutationObserver' in window) {
    var pending = null;
    new MutationObserver(function (records) {
      var found = false;
      records.forEach(function (rec) {
        Array.prototype.forEach.call(rec.addedNodes, function (n) {
          if (n.nodeType !== 1) return;
          if (n.matches && (n.matches('.rv, .rv-stagger, .sweep, .count, .par') ||
                            n.querySelector('.rv, .rv-stagger, .sweep, .count, .par'))) {
            found = true;
          }
        });
      });
      if (!found) return;
      clearTimeout(pending);
      pending = setTimeout(function () { init(document); setTimeout(failsafe, 700); }, 60);
    }).observe(document.body, { childList: true, subtree: true });
  }

  // Last line of defence. Nothing animated should ever be able to leave content
  // permanently invisible: if anything is still unrevealed a moment after load,
  // and it is anywhere near the viewport, just show it.
  function failsafe() {
    var vh = window.innerHeight;
    $$('.rv, .rv-stagger, .sweep').forEach(function (el) {
      if (el.classList.contains('in')) return;
      var r = el.getBoundingClientRect();
      if (r.top < vh * 1.5) el.classList.add('in');
    });
  }
  setTimeout(failsafe, 1200);
  window.addEventListener('load', function () { setTimeout(failsafe, 400); });
})();
